import { useEffect, useState, useRef } from 'react'

/**
 * Hook to detect scroll direction and add classes to body for address bar minimization
 * Similar to nytimes.com behavior - minimizes address bar on scroll down
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Minimum scroll delta to trigger direction change (default: 5)
 * @param {boolean} options.enabled - Whether the hook is enabled (default: true)
 * @returns {Object} - { scrollDirection: 'up' | 'down' | null, isAtTop: boolean }
 */
export function useScrollDirection(options = {}) {
  const { threshold = 5, enabled = true } = options
  const [scrollDirection, setScrollDirection] = useState(null)
  const [isAtTop, setIsAtTop] = useState(true)
  const lastScrollTopRef = useRef(0)
  const scrollTimeoutRef = useRef(null)

  useEffect(() => {
    if (!enabled) return

    let ticking = false

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0
          const scrollDelta = scrollTop - lastScrollTopRef.current

          // Check if at top
          const atTop = scrollTop < 10
          setIsAtTop(atTop)

          // Determine scroll direction
          if (Math.abs(scrollDelta) > threshold) {
            if (scrollDelta > 0) {
              // Scrolling down
              setScrollDirection('down')
              document.body.classList.remove('scrolling-up', 'at-top')
              document.body.classList.add('scrolling-down')
            } else {
              // Scrolling up
              setScrollDirection('up')
              document.body.classList.remove('scrolling-down')
              if (atTop) {
                document.body.classList.add('at-top')
              } else {
                document.body.classList.add('scrolling-up')
              }
            }
          }

          // Always add at-top class when at top
          if (atTop) {
            document.body.classList.add('at-top')
            document.body.classList.remove('scrolling-down', 'scrolling-up')
          }

          lastScrollTopRef.current = scrollTop
          ticking = false
        })
        ticking = true
      }

      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }

      // Reset to 'up' after scrolling stops (helps show address bar when user stops)
      scrollTimeoutRef.current = setTimeout(() => {
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0
        if (currentScrollTop >= 10) {
          document.body.classList.remove('scrolling-down')
          document.body.classList.add('scrolling-up')
        }
      }, 150)
    }

    // Initial check
    const initialScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0
    lastScrollTopRef.current = initialScrollTop
    setIsAtTop(initialScrollTop < 10)
    if (initialScrollTop < 10) {
      document.body.classList.add('at-top')
    }

    // Use passive listener for better performance
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
      // Clean up classes on unmount
      document.body.classList.remove('scrolling-down', 'scrolling-up', 'at-top')
    }
  }, [threshold, enabled])

  return { scrollDirection, isAtTop }
}

